import './App.css';
import { useEffect, useRef } from "react";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import Authentication from './Pages/Authentication/Authentication';
import Logout from './Pages/Logout/Logout';
import UserLinks from './Pages/UserLinks/UserLinks';
import NotFound from './Pages/NotFound/NotFound';
import Home from './Pages/Home/Home';
import Profile from './Pages/Profile/Profile';
import SendPasswordReset from './Pages/SendPasswordReset/SendPasswordReset';
import PasswordReset from './Pages/PasswordReset/PasswordReset';
import TermsAndConditions from './Pages/TermsAndConditions/TermsAndConditions';
import PrivacyPolicy from './Pages/PrivacyPolicy/PrivacyPolicy';
import DefaultLayout from './Components/DefaultLayout/DefaultLayout';
import UserLinksLayout from './Components/UserLinksLayout/UserLinksLayout';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './Components/ProtectedRoute/PrivateRoute';
import { ToastProvider } from './context/ToastContext';
import Snackbar from './Components/Snackbar/Snackbar';
import DashboardLayout from './Components/DashboardLayout/DashboardLayout';
import { APP_VERSION } from './utils/constants';
import Dashboard from './Pages/Dashboard/Dashboard';
import Updates from './Pages/Updates/Updates';
import ScrollToTop from './Components/ScrollToTop/ScrollToTop';

function App() {
  const snackbarRef = useRef();

  useEffect(() => {
    console.log(`Welcome to Linkerto. v${APP_VERSION}`);
  }, []);

  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      type: 'dark',
      primary: {
        main: '#fff',
        contrastText: '#0f1c29',
        dark: '#e8e7e7',
      },
      secondary: {
        main: '#89253e',
        contrastText: '#fff'
      },
      background: {
        default: '#0f1c29',
        paper: '#0b0e14',
      },
      divider: 'rgba(51, 59, 77, 0.6)',
      text: {
        primary: '#fff',
        secondary: 'rgba(255,255,255,0.7)',
        disabled: 'rgba(255,255,255,0.5)',
      },
    },
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily: 'Poppins, sans-serif',
    },
    components: {
      // Container level — 12px
      MuiCard: {
        styleOverrides: { root: { borderRadius: 12 } },
      },
      MuiPaper: {
        styleOverrides: { root: { borderRadius: 12 } },
      },
      MuiMenu: {
        styleOverrides: { paper: { borderRadius: 12 } },
      },
      MuiPopover: {
        styleOverrides: { paper: { borderRadius: 12 } },
      },
      // Overlay level — 16px
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 16 } },
      },
      // Drawer has no radius — it anchors to the screen edge
      MuiDrawer: {
        styleOverrides: { paper: { borderRadius: 0 } },
      },
      // MuiButton: {
      //   styleOverrides: {
      //     sizeLarge: ({ theme }) => ({
      //       [theme.breakpoints.down('sm')]: {
      //         padding: '4px 10px',
      //         fontSize: '0.8125rem',
      //       },
      //     }),
      //   },
      // },
    },
  });

  return (
    <ThemeProvider theme={darkTheme}>
      <ToastProvider snackbarRef={snackbarRef}>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <Routes>
              <Route element={<DefaultLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/signup" element={<Authentication type="create" />} />
                <Route path="/login" element={<Authentication type="login" />} />
                <Route path="/sendPasswordReset" element={<SendPasswordReset />} />
                <Route path="/passwordReset" element={<PasswordReset />} />
                <Route path="/termsAndConditions" element={<TermsAndConditions />} />
                <Route path="/privacyPolicy" element={<PrivacyPolicy />} />
                <Route path="/updates" element={<Updates />} />
                <Route path="/404" element={<NotFound />} />
              </Route>
              <Route element={<ProtectedRoute />}>
                <Route path="/logout" element={<Logout />} />
                <Route path="/profile" element={<Profile />} />
              </Route>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
              </Route>
              <Route element={<UserLinksLayout />}>
                <Route path="/:user" element={<UserLinks />} />
              </Route>
              <Route path="*" element={<Navigate to="/404" />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
        <Snackbar ref={snackbarRef} />
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
